import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Download, Upload, FileSpreadsheet, QrCode, X, Check, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';

const ENTITIES = {
  equipment: { label: 'Equipos', icon: '⚡', importable: true },
  tasks: { label: 'Tareas', icon: '📋', importable: false },
  inventory: { label: 'Inventario', icon: '📦', importable: true },
  budget: { label: 'Presupuesto', icon: '💰', importable: false, requiresProject: true },
  incidents: { label: 'Incidencias', icon: '⚠️', importable: false },
};

export default function ImportExport() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [equipments, setEquipments] = useState([]);
  const [selectedEquipments, setSelectedEquipments] = useState([]);
  const [tab, setTab] = useState('export');
  const [importEntity, setImportEntity] = useState('equipment');
  const [importResult, setImportResult] = useState(null);
  const [qrLabels, setQrLabels] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.data || r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedProject && tab === 'qr') {
      api.get('/equipment', { params: { project_id: selectedProject } })
        .then(r => setEquipments(r.data.data || r.data || []))
        .catch(() => setEquipments([]));
    }
  }, [selectedProject, tab]);

  const exportData = async (entity, format) => {
    try {
      const params = { format };
      if (selectedProject) params.project_id = selectedProject;
      const response = await api.get(`/io/export/${entity}`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${entity}-export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${ENTITIES[entity].label} exportados (${format.toUpperCase()})`);
    } catch (e) { toast.error('Error exportando'); }
  };

  const importData = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const params = {};
      if (selectedProject) params.project_id = selectedProject;
      const { data } = await api.post(`/io/import/${importEntity}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params,
      });
      setImportResult(data);
      toast.success(`Importados: ${data.imported}/${data.total}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error importando');
    } finally { setLoading(false); e.target.value = ''; }
  };

  const generateQR = async (equipId) => {
    window.open(`/api/qr/equipment/${equipId}/label`, '_blank');
  };

  const generateBatchQR = async () => {
    if (selectedEquipments.length === 0) return toast.error('Selecciona al menos un equipo');
    try {
      const { data } = await api.post('/qr/batch', { equipmentIds: selectedEquipments });
      setQrLabels(data);
      toast.success(`${data.length} etiquetas generadas`);
    } catch (e) { toast.error('Error'); }
  };

  const printLabels = () => {
    const win = window.open('', '_blank');
    win.document.write(`<html><head><style>
      body { margin: 0; font-family: Arial; }
      .grid { display: flex; flex-wrap: wrap; gap: 4mm; padding: 5mm; }
      .label { width: 80mm; height: 50mm; border: 0.5px solid #ccc; display: flex; gap: 3mm; padding: 3mm; box-sizing: border-box; page-break-inside: avoid; }
      .label-qr img { width: 42mm; height: 42mm; }
      .label-info { flex: 1; display: flex; flex-direction: column; justify-content: center; gap: 1.5mm; }
      .label-type { font-size: 7pt; font-weight: bold; color: #D4A017; text-transform: uppercase; }
      .label-brand { font-size: 9pt; font-weight: bold; }
      .label-model { font-size: 7pt; color: #333; }
      .label-serial { font-size: 7pt; font-family: monospace; color: #666; }
      .label-proj { font-size: 6pt; color: #999; border-top: 0.5px solid #ddd; padding-top: 1.5mm; margin-top: 1mm; }
      @media print { .grid { padding: 0; } .label { border: none; } }
    </style></head><body><div class="grid">`);

    for (const l of qrLabels) {
      win.document.write(`<div class="label">
        <div class="label-qr"><img src="${l.qrDataUrl}" /></div>
        <div class="label-info">
          <div class="label-type">${l.type}</div>
          <div class="label-brand">${l.brand || ''}</div>
          <div class="label-model">${l.model || ''}</div>
          <div class="label-serial">S/N: ${l.serialNumber || '-'}</div>
          <div class="label-proj">${l.projectCode}</div>
          ${l.zone ? `<div class="label-proj">Zona: ${l.zone}</div>` : ''}
        </div>
      </div>`);
    }

    win.document.write('</div></body></html>');
    win.document.close();
    win.print();
  };

  const toggleEquipment = (id) => {
    setSelectedEquipments(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    if (selectedEquipments.length === equipments.length) setSelectedEquipments([]);
    else setSelectedEquipments(equipments.map(e => e.id));
  };

  return (
    <div className="io-page">
      <div className="page-header">
        <div><h1><FileSpreadsheet size={24} /> Import / Export</h1><p className="subtitle">Datos masivos y etiquetas QR</p></div>
      </div>

      <div className="filters-bar">
        <select className="filter-select" value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
          <option value="">Todos los proyectos</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
        </select>
        <div className="tab-buttons">
          <button className={`tab-btn ${tab === 'export' ? 'active' : ''}`} onClick={() => setTab('export')}>Exportar</button>
          <button className={`tab-btn ${tab === 'import' ? 'active' : ''}`} onClick={() => setTab('import')}>Importar</button>
          <button className={`tab-btn ${tab === 'qr' ? 'active' : ''}`} onClick={() => setTab('qr')}>Etiquetas QR</button>
        </div>
      </div>

      {/* Export Tab */}
      {tab === 'export' && (
        <div className="io-export-grid">
          {Object.entries(ENTITIES).map(([key, ent]) => (
            <div key={key} className="io-export-card">
              <span className="io-export-icon">{ent.icon}</span>
              <h3>{ent.label}</h3>
              <div className="io-export-actions">
                <button className="btn btn-sm btn-primary" onClick={() => exportData(key, 'xlsx')}><Download size={12} /> XLSX</button>
                <button className="btn btn-sm btn-secondary" onClick={() => exportData(key, 'csv')}><Download size={12} /> CSV</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Import Tab */}
      {tab === 'import' && (
        <div className="io-import-section">
          <div className="io-import-card">
            <h3><Upload size={18} /> Importar datos</h3>
            <div className="form-group">
              <label>Entidad</label>
              <select value={importEntity} onChange={e => setImportEntity(e.target.value)}>
                {Object.entries(ENTITIES).filter(([, v]) => v.importable).map(([k, v]) => (
                  <option key={k} value={k}>{v.icon} {v.label}</option>
                ))}
              </select>
            </div>
            {importEntity === 'equipment' && !selectedProject && (
              <div className="io-warn"><AlertTriangle size={14} /> Selecciona un proyecto antes de importar equipos</div>
            )}
            <div className="io-upload-area">
              <input type="file" accept=".csv,.xlsx,.xls" onChange={importData} disabled={loading || (importEntity === 'equipment' && !selectedProject)} />
              <p>Arrastra un archivo CSV o XLSX aquí</p>
              <span>Máx. 10 MB</span>
            </div>
            <div className="io-import-help">
              <h4>Columnas esperadas ({ENTITIES[importEntity]?.label}):</h4>
              {importEntity === 'equipment' && <p>Tipo, Marca, Modelo, N/S, Barcode, Zona, String, Fila, Posición, Estado, Notas</p>}
              {importEntity === 'inventory' && <p>Categoría, Nombre, SKU, Marca, Modelo, Unidad, Cantidad, Mínimo, Coste, Proveedor</p>}
              <p className="io-import-tip">Los nombres de columnas pueden ser en español o inglés. Se auto-detecta el separador (;  o ,).</p>
            </div>
          </div>

          {importResult && (
            <div className="io-import-result">
              <h3><Check size={16} style={{ color: '#10B981' }} /> Resultado de importación</h3>
              <div className="io-result-stats">
                <span className="io-result-ok">{importResult.imported} importados</span>
                <span className="io-result-total">de {importResult.total} filas</span>
                {importResult.errors?.length > 0 && <span className="io-result-err">{importResult.errors.length} errores</span>}
              </div>
              {importResult.errors?.length > 0 && (
                <div className="io-errors">
                  {importResult.errors.map((e, i) => (
                    <div key={i} className="io-error-row">Fila {e.row}: {e.error}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* QR Tab */}
      {tab === 'qr' && (
        <div className="io-qr-section">
          {!selectedProject ? (
            <div className="empty-state"><QrCode size={48} /><h3>Selecciona un proyecto</h3><p>Para ver los equipos y generar etiquetas QR</p></div>
          ) : (
            <>
              <div className="io-qr-toolbar">
                <label className="io-qr-select-all">
                  <input type="checkbox" checked={selectedEquipments.length === equipments.length && equipments.length > 0} onChange={toggleAll} />
                  Seleccionar todos ({equipments.length})
                </label>
                <button className="btn btn-primary btn-sm" onClick={generateBatchQR} disabled={selectedEquipments.length === 0}>
                  <QrCode size={14} /> Generar {selectedEquipments.length} etiquetas
                </button>
                {qrLabels.length > 0 && (
                  <button className="btn btn-secondary btn-sm" onClick={printLabels}>
                    🖨️ Imprimir etiquetas
                  </button>
                )}
              </div>

              <div className="io-qr-list">
                {equipments.map(eq => (
                  <div key={eq.id} className={`io-qr-item ${selectedEquipments.includes(eq.id) ? 'selected' : ''}`}>
                    <input type="checkbox" checked={selectedEquipments.includes(eq.id)} onChange={() => toggleEquipment(eq.id)} />
                    <div className="io-qr-info">
                      <span className="io-qr-type">{eq.type}</span>
                      <span className="io-qr-name">{eq.brand} {eq.model}</span>
                      <span className="io-qr-serial">S/N: {eq.serial_number || '-'}</span>
                    </div>
                    <button className="btn btn-sm btn-secondary" onClick={() => generateQR(eq.id)}><QrCode size={12} /> Ver QR</button>
                  </div>
                ))}
                {equipments.length === 0 && <div className="empty-state"><h3>Sin equipos registrados</h3></div>}
              </div>

              {/* Preview labels */}
              {qrLabels.length > 0 && (
                <div className="io-qr-preview">
                  <h3>Vista previa ({qrLabels.length} etiquetas)</h3>
                  <div className="io-qr-preview-grid">
                    {qrLabels.map(l => (
                      <div key={l.id} className="io-qr-label">
                        <img src={l.qrDataUrl} alt="QR" />
                        <div>
                          <span className="io-qr-type">{l.type}</span>
                          <span className="io-qr-name">{l.brand} {l.model}</span>
                          <span className="io-qr-serial">S/N: {l.serialNumber || '-'}</span>
                          <span className="io-qr-serial">{l.projectCode}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
